// Pure UI mapping schema for DEWS III clinical signs.
// No Angular dependencies. No clinical threshold evaluation (see driver-thresholds.ts).
// TypeScript 3.5.3 compatible: no optional chaining (?.), no nullish coalescing (??).

import { Sign } from '../../../../core/schemas/subscription-plan.schema';
import { DriverSubKey } from './dews-iii-drivers/dews-iii-drivers.component';

export { Sign } from '../../../../core/schemas/subscription-plan.schema';
export { DriverSubKey } from './dews-iii-drivers/dews-iii-drivers.component';

// All unique DEWS III sign keys in clinical order (homeostasis-first, then drivers by category).
export const ALL_DEWS_SIGN_KEYS: string[] = [
  'NITBUT',
  'ITBUT',
  'OSMOLARITY',
  'FLUORESCEIN_STAINING',
  'CORNEAL_STAINING_PATTERN',
  'CORNEAL_STAINING_EXTENT',
  'LWE',
  'LIPID_LAYER',
  'MGE_EXPRESSIBILITY',
  'MGE_SECRETION',
  'TEAR_MENISCUS_HEIGHT',
  'SCHIRMERS_TEST',
  'PHENOL_RED',
  'SMTUBE',
  'PARTIAL_BLINKING_FREQUENCY',
  'LAGOPHTHALMUS',
  'COLLARETTES',
  'MEIBOGRAPHY_UPPER',
  'MEIBOGRAPHY_LOWER',
  'MG_ORIFICE_CAPPED_GLANDS',
  'EYELID_TELANGIECTASIA',
  'KERATINIZATION',
  'EYE_MARKUP',
  'CONJUNCTIVAL_CHALASIS',
  'CORNEAL_SENSATION',
  'BULBAR_REDNESS',
  'MMP9',
  'LID_REDNESS'
];

export const HOMEOSTASIS_SIGN_KEYS = {
  tearFilmStability: ['NITBUT', 'ITBUT'],
  osmolarity: ['OSMOLARITY'],
  ocularSurfaceStaining: ['FLUORESCEIN_STAINING', 'CORNEAL_STAINING_PATTERN', 'CORNEAL_STAINING_EXTENT', 'LWE']
};

export type HomeostasisCategoryKey = 'tearFilmStability' | 'osmolarity' | 'ocularSurfaceStaining';

// Union of Sign enum members used in the DEWS III workflow.
export type DewsSignKey =
  // Tear Film drivers
  | Sign.LIPID_LAYER
  | Sign.MGE_EXPRESSIBILITY
  | Sign.MGE_SECRETION
  | Sign.TEAR_MENISCUS_HEIGHT
  | Sign.SCHIRMERS_TEST
  | Sign.PHENOL_RED
  | Sign.SMTUBE
  | Sign.CORNEAL_STAINING_EXTENT
  // Eyelid Anomaly drivers
  | Sign.PARTIAL_BLINKING_FREQUENCY
  | Sign.LAGOPHTHALMUS
  | Sign.COLLARETTES
  | Sign.MEIBOGRAPHY_UPPER
  | Sign.MEIBOGRAPHY_LOWER
  | Sign.MG_ORIFICE_CAPPED_GLANDS
  | Sign.EYELID_TELANGIECTASIA
  | Sign.KERATINIZATION
  // Ocular Surface drivers
  | Sign.EYE_MARKUP
  | Sign.CONJUNCTIVAL_CHALASIS
  | Sign.CORNEAL_SENSATION
  | Sign.LWE
  | Sign.BULBAR_REDNESS
  | Sign.MMP9
  | Sign.LID_REDNESS
  // Homeostasis-only signs
  | Sign.NITBUT
  | Sign.ITBUT
  | Sign.OSMOLARITY
  | Sign.FLUORESCEIN_STAINING
  | Sign.CORNEAL_STAINING_PATTERN;

// Forward mapping: driver → associated sign keys.
// MGE_EXPRESSIBILITY and MGE_SECRETION appear in both lipid and lidMargin.
// CORNEAL_STAINING_EXTENT appears in both mucinGlycocalyx and cellDamageDisruption.
export const DRIVER_SIGN_SCHEMA: Record<DriverSubKey, DewsSignKey[]> = {
  lipid: [Sign.LIPID_LAYER, Sign.MGE_EXPRESSIBILITY, Sign.MGE_SECRETION],
  aqueous: [Sign.TEAR_MENISCUS_HEIGHT, Sign.SCHIRMERS_TEST, Sign.PHENOL_RED, Sign.SMTUBE],
  mucinGlycocalyx: [Sign.CORNEAL_STAINING_EXTENT],
  blinkMechanism: [Sign.PARTIAL_BLINKING_FREQUENCY, Sign.LAGOPHTHALMUS],
  lidMargin: [
    Sign.COLLARETTES,
    Sign.MEIBOGRAPHY_UPPER,
    Sign.MEIBOGRAPHY_LOWER,
    Sign.MG_ORIFICE_CAPPED_GLANDS,
    Sign.EYELID_TELANGIECTASIA,
    Sign.MGE_EXPRESSIBILITY,
    Sign.MGE_SECRETION,
    Sign.KERATINIZATION
  ],
  anatomicalAlignment: [Sign.EYE_MARKUP, Sign.CONJUNCTIVAL_CHALASIS],
  neuralDysfunction: [Sign.CORNEAL_SENSATION],
  cellDamageDisruption: [Sign.FLUORESCEIN_STAINING, Sign.CORNEAL_STAINING_EXTENT, Sign.LWE],
  inflammationOxidativeStress: [Sign.BULBAR_REDNESS, Sign.MMP9, Sign.LID_REDNESS]
};

// Forward mapping: homeostasis marker category → associated sign keys.
// CORNEAL_STAINING_EXTENT and LWE overlap with driver signs above.
export const HOMEOSTASIS_SIGN_SCHEMA: Record<HomeostasisCategoryKey, DewsSignKey[]> = {
  tearFilmStability: [Sign.NITBUT, Sign.ITBUT],
  osmolarity: [Sign.OSMOLARITY],
  ocularSurfaceStaining: [
    Sign.FLUORESCEIN_STAINING,
    Sign.CORNEAL_STAINING_PATTERN,
    Sign.CORNEAL_STAINING_EXTENT,
    Sign.LWE
  ]
};

// Reverse lookup payload for a single sign.
// Empty drivers array means a pure homeostasis sign (e.g. Sign.NITBUT).
// Empty homeostasisMarkers array means a pure driver sign (e.g. Sign.LIPID_LAYER).
export interface DedSignAssociation {
  drivers: DriverSubKey[];
  homeostasisMarkers: HomeostasisCategoryKey[];
}

// Reverse lookup: sign key → { drivers, homeostasisMarkers }.
// Derived from the forward schemas above — no separate key listing needed.
export const DED_SIGN_ASSOCIATION_MAP: Record<DewsSignKey, DedSignAssociation> = (function() {
  const map: { [key: string]: DedSignAssociation } = {};

  (Object.keys(DRIVER_SIGN_SCHEMA) as DriverSubKey[]).forEach(function(driver) {
    DRIVER_SIGN_SCHEMA[driver].forEach(function(sign) {
      if (!map[sign]) {
        map[sign] = { drivers: [], homeostasisMarkers: [] };
      }
      map[sign].drivers.push(driver);
    });
  });

  (Object.keys(HOMEOSTASIS_SIGN_SCHEMA) as HomeostasisCategoryKey[]).forEach(function(marker) {
    HOMEOSTASIS_SIGN_SCHEMA[marker].forEach(function(sign) {
      if (!map[sign]) {
        map[sign] = { drivers: [], homeostasisMarkers: [] };
      }
      map[sign].homeostasisMarkers.push(marker);
    });
  });

  return map as Record<DewsSignKey, DedSignAssociation>;
})();
